import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  Image,
  IconButton,
  Textarea,
  useDisclosure,
  useToast,
  Button,
  Code,
  Link,
  List,
  ListItem,
  OrderedList,
  UnorderedList,
  Heading,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useOutsideClick,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import setting_icon from '../Assets/icons/setting_icon.svg';
import ArrowIconRight from '../Assets/icons/ArrowIconRight.svg';
import toolboxIcon from '../Assets/icons/toolboxIcon.svg';
import sidebar_icon from '../Assets/icons/sidebar_icon.svg';
import CarSoundDiagnosisPopup from './CarSoundDiagnosisPopup';
import { useSendImageMessage, useSendVoiceMessage, useSendMessage, useGetSuggestions } from '../utils/conversation.api';
// import { useCreateSuggestion } from '../utils/conversation.api';
import ExpertChatBot from './ExpertChatBot';
import WaveformAudioPlayer from './WaveformAudioPlayer';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '../store/chatStore';
import ChatLoadingDots from './ChatLoadingDots';
import { useUIStore } from '../store/uiStore';
import ChatInputSection from './ChatInputSection';

const ChatBox = ({ isMobile, chatMessages, onChatSubmit, conversationId, onProcessManagement, selectedVehicle, vehicleContext, messagesLoading }) => {
  console.log('ChatBox rendered with props:', { isMobile, chatMessages, onChatSubmit, conversationId, vehicleContext, messagesLoading });
  console.log('ChatBox chatMessages length:', chatMessages ? chatMessages.length : 'No messages');
  console.log('ChatBox loading state:', { messagesLoading, conversationId, filteredMessagesCount: chatMessages?.length || 0 });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAudioMenuOpen, onOpen: onAudioMenuOpen, onClose: onAudioMenuClose } = useDisclosure();
  const { isOpen: isRecordingModalOpen, onOpen: onRecordingModalOpen, onClose: onRecordingModalClose } = useDisclosure();
  const { toggleSidebar, toggleMobileDrawer } = useUIStore();
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mobileMessagesRef = useRef(null);
  const desktopMessagesRef = useRef(null);
  const mobileTextareaRef = useRef(null);
  const desktopTextareaRef = useRef(null);
  const toast = useToast();
  const [showExpertChat, setShowExpertChat] = useState(false);
  const [isDiagnoseMode, setIsDiagnoseMode] = useState(false);
  const [isBuySellMode, setIsBuySellMode] = useState(false);
  const [isHelpMode, setIsHelpMode] = useState(false);
  const navigate = useNavigate();
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [pendingRecording, setPendingRecording] = useState(null);
  const recordingTimerRef = useRef(null);
  const hasManuallySelectedMode = useRef(false);
  const lastAutoSelectedConversationId = useRef(null);
  const previousConversationIdRef = useRef(conversationId);
  
  // Detect iOS device
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Get email from localStorage with 'gmail' key (or fallback to 'email')
  const loggedInUserEmail = localStorage.getItem('gmail') || localStorage.getItem('email') || 'user@email.com';
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  
  // Chat store for adding messages
  const { addChatMessage } = useChatStore();
  
  const { mutate: sendImageMessage, isLoading: isSendingImage } = useSendImageMessage();
  const { mutate: sendVoiceMessage, isLoading: isSendingVoice } = useSendVoiceMessage();
  const { mutate: sendMessage, isLoading: isSendingMessage } = useSendMessage();
  
  // Determine current swimlane based on active mode
  const getCurrentSwimlane = () => {
    if (isDiagnoseMode) {
      return 'diagnose';
    } else if (isBuySellMode) {
      return 'buy/sell';
    } else if (isHelpMode) {
      return 'help';
    }
    return 'text';
  };
  
  const currentSwimlane = getCurrentSwimlane();
  const { data: suggestionsData, refetch: refetchSuggestions } = useGetSuggestions(selectedVehicle?.id, currentSwimlane);
  // const { mutate: createSuggestion } = useCreateSuggestion();
  
  // Extract suggestions array from API response
  const suggestions = suggestionsData && Array.isArray(suggestionsData) 
    ? suggestionsData 
    : suggestionsData?.suggestions || suggestionsData?.data || [];

  // Function to filter messages based on message_type
  // When message_type is 'buy', 'sell', or 'repair', only show agent messages
  // For all other message types, show both user and agent messages
  const getFilteredMessages = () => {
    if (!chatMessages || !Array.isArray(chatMessages)) return [];
    
    const filtered = chatMessages.filter(msg => {
      // If message has originalData with message_type, check if we should filter
      if (msg.originalData && msg.originalData.message_type) {
        const messageType = msg.originalData.message_type.toLowerCase();
        
        // For buy, sell, repair message types, only show agent messages
        if (['buy', 'sell', 'repair'].includes(messageType)) {
          const shouldShow = msg.type === 'ai'; // Only show AI/agent messages
          if (!shouldShow) {
            console.log(`Filtering out ${msg.type} message for message_type: ${messageType}`);
          }
          return shouldShow;
        }
      }
      
      // For all other cases, show both user and agent messages
      return true;
    });
    
    console.log('Message filtering:', {
      total: chatMessages.length,
      filtered: filtered.length,
      filteredOut: chatMessages.length - filtered.length
    });
    
    return filtered;
  };

  // Get filtered messages for display
  const filteredMessages = getFilteredMessages();

  const renderVehicleContextBanner = () => {
    if (!vehicleContext) return null;

    return (
      <Box
        zIndex={isMobile ? 100 : 10}
        py={1}
        mb={4}
        width="100%"
        display="flex"
        justifyContent="center"
        px={isMobile ? 2 : 0}
        // bg="white"
        sx={{
          position: '-webkit-sticky', // iOS Safari support - must come first
          position: 'sticky',
          top: 0,
          // iOS Safari optimizations for better sticky positioning
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          willChange: 'transform',
          // Ensure it stays on top during scroll
          isolation: 'isolate',
          // Force hardware acceleration on iOS
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          // Ensure proper stacking context
          WebkitPerspective: 1000,
          perspective: 1000,
          // Ensure it doesn't get clipped
          overflow: 'visible',
          // Extend background to cover full width of scroll container
          // Mobile: scroll container has px={4} = 16px, so we need -16px margin
          // Desktop: scroll container has px={8} = 32px, so we need -32px margin
          marginLeft: isMobile ? '-16px' : '-32px',
          marginRight: isMobile ? '-16px' : '-32px',
          paddingLeft: isMobile ? '16px' : '32px',
          paddingRight: isMobile ? '16px' : '32px',
          // Add top padding to prevent content from showing through
          paddingTop: '8px',
          marginTop: '-8px',
        }}
      >
        <Box
          bg="#08e26e"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="full"
          boxShadow="sm"
          px={isMobile ? 3 : 4}
          py={isMobile ? 1.5 : 2}
          width={isMobile ? "100%" : "fit-content"}
          maxW={isMobile ? "100%" : "none"}
          display="flex"
          alignItems="center"
          gap={isMobile ? 2 : 3}
          flexShrink={0}
        >
          {/* Toggle Sidebar Button */}
          <IconButton
            icon={<Image src={sidebar_icon} alt="Toggle Sidebar" w={isMobile ? "4" : "5"} h={isMobile ? "4" : "5"} />}
            variant="ghost"
            onClick={handleSidebarToggle}
            aria-label="Toggle Sidebar"
            size={isMobile ? "sm" : "md"}
            _hover={{ bg: 'rgba(255, 255, 255, 0.2)' }}
            minW="auto"
            h="auto"
            p={1}
            flexShrink={0}
          />
          
          <Text 
            fontSize={isMobile ? "xs" : "sm"} 
            color="#212e89" 
            textAlign="center"
            wordBreak="break-word"
            overflowWrap="break-word"
            whiteSpace={isMobile ? "normal" : "nowrap"}
            overflow={isMobile ? "visible" : "hidden"}
            textOverflow={isMobile ? "clip" : "ellipsis"}
            flex={1}
            minW={0}
            lineHeight={isMobile ? "1.4" : "1.5"}
          >
            {vehicleContext}
          </Text>
        </Box>
      </Box>
    );
  };

  // Function to render markdown with react-markdown
  const renderMarkdownMessage = (message) => {
    if (typeof message !== 'string') return message;
    
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          // Custom components for better styling
          h1: ({ children }) => (
            <Heading as="h1" size="lg" color="blue.700" mb={3} mt={4} wordBreak="break-word" overflowWrap="break-word">
              {children}
            </Heading>
          ),
          h2: ({ children }) => (
            <Heading as="h2" size="md" color="blue.600" mb={2} mt={3} wordBreak="break-word" overflowWrap="break-word">
              {children}
            </Heading>
          ),
          h3: ({ children }) => (
            <Heading as="h3" size="sm" color="blue.500" mb={2} mt={2} wordBreak="break-word" overflowWrap="break-word">
              {children}
            </Heading>
          ),
          p: ({ children }) => (
            <Text fontSize="sm" mb={2} color="black" lineHeight="1.5" wordBreak="break-word" overflowWrap="break-word" whiteSpace="pre-wrap">
              {children}
            </Text>
          ),
          ul: ({ children }) => (
            <UnorderedList spacing={1} ml={4} mb={2}>
              {children}
            </UnorderedList>
          ),
          ol: ({ children }) => (
            <OrderedList spacing={1} ml={4} mb={2}>
              {children}
            </OrderedList>
          ),
          li: ({ children }) => (
            <ListItem fontSize="sm" color="black" wordBreak="break-word" overflowWrap="break-word">
              {children}
            </ListItem>
          ),
          code: ({ inline, children }) => (
            <Code
              fontSize="xs"
              bg="gray.100"
              color="gray.800"
              px={1}
              py={0.5}
              borderRadius="md"
              fontFamily="mono"
              wordBreak="break-word"
              overflowWrap="break-word"
            >
              {children}
            </Code>
          ),
          pre: ({ children }) => (
            <Box
              as="pre"
              bg="gray.100"
              p={3}
              borderRadius="md"
              overflow="auto"
              fontSize="xs"
              fontFamily="mono"
              mb={2}
              wordBreak="break-word"
              overflowWrap="break-word"
              whiteSpace="pre-wrap"
            >
              {children}
            </Box>
          ),
          blockquote: ({ children }) => (
            <Box
              borderLeft="4px"
              borderColor="blue.300"
              pl={4}
              py={2}
              bg="blue.50"
              borderRadius="md"
              mb={2}
              fontStyle="italic"
            >
              {children}
            </Box>
          ),
          a: ({ href, children }) => (
            <Link href={href} color="blue.500" isExternal>
              {children}
            </Link>
          ),
          table: ({ children }) => (
            <Box overflow="auto" mb={2}>
              <Box as="table" width="100%" border="1px" borderColor="gray.200" borderRadius="md">
                {children}
              </Box>
            </Box>
          ),
          thead: ({ children }) => (
            <Box as="thead" bg="gray.50">
              {children}
            </Box>
          ),
          tbody: ({ children }) => (
            <Box as="tbody">
              {children}
            </Box>
          ),
          tr: ({ children }) => (
            <Box as="tr" borderBottom="1px" borderColor="gray.200">
              {children}
            </Box>
          ),
          th: ({ children }) => (
            <Box as="th" px={3} py={2} textAlign="left" fontWeight="bold" fontSize="sm">
              {children}
            </Box>
          ),
          td: ({ children }) => (
            <Box as="td" px={3} py={2} fontSize="sm">
              {children}
            </Box>
          ),
          hr: () => <Divider my={3} />,
          strong: ({ children }) => (
            <Text as="span" fontWeight="bold" wordBreak="break-word" overflowWrap="break-word">
              {children}
            </Text>
          ),
          em: ({ children }) => (
            <Text as="span" fontStyle="italic" wordBreak="break-word" overflowWrap="break-word">
              {children}
            </Text>
          ),
        }}
      >
        {message}
      </ReactMarkdown>
    );
  };


  // Reset textarea heights when conversation changes
  useEffect(() => {
    resetTextareaHeight();
    // Reset scroll tracking when conversation changes
    setUserHasScrolled(false);
    // Reset all modes when conversation changes
    setIsDiagnoseMode(false);
    setIsBuySellMode(false);
    setIsHelpMode(false);
    // Reset manual selection tracking when conversation changes
    hasManuallySelectedMode.current = false;
    lastAutoSelectedConversationId.current = null;
    
    // Track conversation change
    const conversationChanged = previousConversationIdRef.current !== conversationId;
    if (conversationChanged) {
      previousConversationIdRef.current = conversationId;
      console.log('Conversation changed:', conversationId);
    }
  }, [conversationId]);

  // Fetch suggestions when conversation opens, vehicle changes, or swimlane changes
  useEffect(() => {
    if (conversationId && selectedVehicle?.id && currentSwimlane) {
      console.log('Fetching suggestions with:', { vehicle_id: selectedVehicle.id, swimlane: currentSwimlane });
      refetchSuggestions();
    }
  }, [conversationId, selectedVehicle?.id, currentSwimlane, refetchSuggestions]);

  // Auto-select button based on message_type from the last message
  // Updates when messages change or conversation changes
  useEffect(() => {
    if (!chatMessages || !Array.isArray(chatMessages) || chatMessages.length === 0) {
      return;
    }

    if (!conversationId) {
      return;
    }

    // Don't auto-select if user has manually selected a mode
    if (hasManuallySelectedMode.current) {
      return;
    }

    // Track the last message we processed to avoid re-processing the same message
    // Only skip if we've already processed this exact conversation and message count
    const currentMessageCount = chatMessages.length;
    const lastProcessedKey = `${conversationId}_${currentMessageCount}`;
    if (lastAutoSelectedConversationId.current === lastProcessedKey) {
      return;
    }

    // Check the LAST message (most recent) in the array for message_type
    const lastMessage = chatMessages[chatMessages.length - 1];
    
    if (!lastMessage) {
      return;
    }
    
    // Verify message belongs to current conversation (if conversation_id is available)
    if (lastMessage.originalData?.conversation_id && lastMessage.originalData.conversation_id !== conversationId) {
      console.log('Last message belongs to different conversation:', lastMessage.originalData.conversation_id, 'current:', conversationId);
      return;
    }
    
    // Get message_type from the last message
    if (lastMessage.originalData && lastMessage.originalData.message_type) {
      const messageType = lastMessage.originalData.message_type.toLowerCase();
      
      if (messageType === 'text') {
        // If message_type is "text", reset to default (no swimlane selected)
        setIsDiagnoseMode(false);
        setIsBuySellMode(false);
        setIsHelpMode(false);
        lastAutoSelectedConversationId.current = lastProcessedKey;
        console.log('Reset to default mode (text) based on last message_type for conversation:', conversationId);
        return;
      } else if (messageType === 'diagnose') {
        // If message_type is "diagnose", activate Diagnose Repair Vehicle button
        setIsDiagnoseMode(true);
        setIsBuySellMode(false);
        setIsHelpMode(false);
        lastAutoSelectedConversationId.current = lastProcessedKey;
        console.log('Auto-selected Diagnose Repair Vehicle button based on last message_type for conversation:', conversationId);
        return;
      } else if (messageType === 'buy/sell') {
        // If message_type is "buy/sell", activate Vehicle Value button
        setIsBuySellMode(true);
        setIsDiagnoseMode(false);
        setIsHelpMode(false);
        lastAutoSelectedConversationId.current = lastProcessedKey;
        console.log('Auto-selected Vehicle Value button based on last message_type for conversation:', conversationId);
        return;
      }
    }
    
    // If no message_type found in last message, ensure we're in default mode (text)
    lastAutoSelectedConversationId.current = lastProcessedKey;
    setIsDiagnoseMode(false);
    setIsBuySellMode(false);
    setIsHelpMode(false);
    console.log('No message_type found in last message, reset to default mode for conversation:', conversationId);
  }, [chatMessages, conversationId]);

  // Handle action button clicks
  const handleActionButtonClick = (actionType) => {
    let messageText = '';
    let messageType = '';
    
    switch (actionType) {
      case 'buy':
        messageText = 'Alright you want to buy this car tell me how can I help you';
        messageType = 'buy';
        break;
      case 'repair':
        messageText = 'got it you want to do maintanince of your car tell me how can i helo' ;
        messageType = 'repair';
        break;
      case 'learn':
        messageText = 'Alright you want to sell this car tell me how can I help you';
        messageType = 'sell';
        break;
      default:
        return;
    }
    
    console.log('Action button clicked:', { actionType, messageText, messageType });
    
    // Reset scroll tracking when action button is clicked
    setUserHasScrolled(false);
    
    // Send the message directly to backend without showing user message in UI
    if (conversationId) {
      console.log('Sending message directly to backend:', { messageText, messageType });
      sendMessage({
        conversationId,
        messageData: {
          message: messageText,
          message_type: messageType
        }
      });
    } else {
      console.error('No conversation ID available');
      toast({
        title: 'Error',
        description: 'No conversation ID available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
      if (isMobile && mobileMessagesRef.current) {
        mobileMessagesRef.current.scrollTo({
          top: mobileMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      } else if (!isMobile && desktopMessagesRef.current) {
        desktopMessagesRef.current.scrollTo({
          top: desktopMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    };

    // Only auto-scroll if user hasn't manually scrolled up
    if (!userHasScrolled) {
      console.log('Auto-scrolling to bottom');
      // Scroll immediately when messages change
      scrollToBottom();
      
      // Also scroll after a small delay to ensure content is rendered
      const timeoutId = setTimeout(scrollToBottom, 100);
      
      return () => clearTimeout(timeoutId);
    } else {
      console.log('Not auto-scrolling - user has scrolled up');
    }
  }, [filteredMessages, isMobile, userHasScrolled]);

  console.log('chat messages', chatMessages)
  console.log('filtered messages', filteredMessages)

  // Handle scroll events to show/hide scroll to bottom button
  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    // Show scroll button if not near bottom
    setShowScrollButton(!isNearBottom);
    
    // Track if user has manually scrolled up
    if (!isNearBottom) {
      if (!userHasScrolled) {
        console.log('User scrolled up - setting userHasScrolled to true');
        setUserHasScrolled(true);
      }
    } else {
      // Reset the flag when user scrolls back to bottom
      if (userHasScrolled) {
        console.log('User scrolled to bottom - resetting userHasScrolled to false');
        setUserHasScrolled(false);
      }
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (isMobile && mobileMessagesRef.current) {
      mobileMessagesRef.current.scrollTo({
        top: mobileMessagesRef.current.scrollHeight,
        behavior: 'smooth'
      });
    } else if (!isMobile && desktopMessagesRef.current) {
      desktopMessagesRef.current.scrollTo({
        top: desktopMessagesRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    
    // Reset scroll tracking when user manually scrolls to bottom
    setUserHasScrolled(false);
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  };

  const handleCarIconClick = () => {
    console.log('Car icon clicked!');
    // On iOS, show menu with options. On Android, directly open file picker (which shows options natively)
    if (isIOS) {
      onAudioMenuOpen();
    } else {
      audioInputRef.current.click();
    }
  };

  const handleBrowseAudio = () => {
    onAudioMenuClose();
    audioInputRef.current.click();
  };

  const focusMessageInput = () => {
    if (isMobile) {
      mobileTextareaRef.current?.focus();
    } else {
      desktopTextareaRef.current?.focus();
    }
  };

  const handleRecordAudio = () => {
    onAudioMenuClose();
    onRecordingModalOpen();
    setPendingRecording(null);
  };

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      
      // Get the best supported audio format
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }
      
      const mediaRecorderInstance = new MediaRecorder(stream, { mimeType });
      const chunks = [];

      mediaRecorderInstance.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorderInstance.onstop = () => {
        // Determine file extension and type based on MIME type
        let fileExtension = 'webm';
        let fileType = 'audio/webm';
        if (mimeType.includes('mp4')) {
          fileExtension = 'm4a';
          fileType = 'audio/mp4';
        } else if (mimeType.includes('ogg')) {
          fileExtension = 'ogg';
          fileType = 'audio/ogg';
        }
        
        const audioBlob = new Blob(chunks, { type: fileType });
        
        // Create a File object from the Blob
        const audioFile = new File([audioBlob], `recording_${Date.now()}.${fileExtension}`, {
          type: fileType
        });
        
        setPendingRecording(audioFile);
        
        // Stop all tracks
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        setAudioStream(null);
        setMediaRecorder(null);
        setIsRecording(false);
        setRecordingTime(0);
        
        toast({
          title: 'Recording complete',
          description: 'Preview the recording below and save when ready.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      };

      mediaRecorderInstance.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        toast({
          title: 'Recording error',
          description: 'An error occurred while recording. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setIsRecording(false);
        setRecordingTime(0);
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        setAudioStream(null);
      };

      mediaRecorderInstance.start();
      setMediaRecorder(mediaRecorderInstance);
      setAudioChunks(chunks);
      setIsRecording(true);
      setRecordingTime(0);
      setPendingRecording(null);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: 'Microphone access denied',
        description: 'Please allow microphone access to record audio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onRecordingModalClose();
      setAudioStream(null);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const handleSaveRecording = () => {
    if (!pendingRecording) return;
    setSelectedAudio(pendingRecording);
    setPendingRecording(null);
    onRecordingModalClose();
    toast({
      title: 'Recording ready',
      description: 'Audio added to the message. Press Enter or click Send to upload.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    focusMessageInput();
  };

  const handleRetakeRecording = () => {
    setPendingRecording(null);
    setRecordingTime(0);
  };

  const cancelRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
    }
    // Stop all tracks
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
    setMediaRecorder(null);
    setAudioChunks([]);
    setPendingRecording(null);
    onRecordingModalClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mediaRecorder, isRecording, audioStream]);

  const handleImageButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleProcessManagement = () => {
    if (onProcessManagement) {
      onProcessManagement();
    } else {
      console.log('Process Management clicked - handler not provided');
    }
  };

  const handleProcessManagementBeta = () => {
    if (onProcessManagement) {
      onProcessManagement();
    } else {
      console.log('Process Management clicked - handler not provided');
    }
  };

  // Handler for Diagnose Repair Vehicle button - Toggle diagnose mode
  const handleDiagnoseRepairVehicle = () => {
    setIsDiagnoseMode(!isDiagnoseMode);
    setIsBuySellMode(false);
    setIsHelpMode(false);
    hasManuallySelectedMode.current = true; // Mark as manually selected
    
    toast({
      title: isDiagnoseMode ? 'Diagnose Mode OFF' : 'Diagnose Mode ON',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Handler for Buy/Sell button - Toggle buy/sell mode
  const handleBuySell = () => {
    setIsBuySellMode(!isBuySellMode);
    setIsDiagnoseMode(false);
    setIsHelpMode(false);
    hasManuallySelectedMode.current = true; // Mark as manually selected
    
    toast({
      title: isBuySellMode ? 'Vehicle Value Mode OFF' : 'Vehicle Value Mode ON',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Handler for Help button - Toggle help mode
  const handleHelp = () => {
    setIsHelpMode(!isHelpMode);
    setIsDiagnoseMode(false);
    setIsBuySellMode(false);
    hasManuallySelectedMode.current = true; // Mark as manually selected
    
    toast({
      title: isHelpMode ? 'Help Mode OFF' : 'Help Mode ON',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleGoToGarage = () => {
    if (selectedVehicle && selectedVehicle.id) {
      navigate(`/carinfo?vehicleId=${selectedVehicle.id}`);
    } else {
      toast({
        title: 'No vehicle selected',
        description: 'Please select a vehicle to view its information.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedImageTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please select a JPG, JPEG, or PNG image file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = ''; // Reset input to allow selecting the same file again
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please select an image smaller than 10MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = ''; // Reset input to allow selecting the same file again
        return;
      }
      
      setSelectedImage(file);
      
      // Show success message
      toast({
        title: 'Image selected',
        description: `${file.name} has been selected. Now type your question and press Enter to send.`,
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
      
      // If there's text in the input, send the image with the text
      if (newMessage.trim()) {
        handleSendImageMessage(file, newMessage);
      }
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 25MB for audio)
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please select an audio file smaller than 25MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = ''; // Reset input to allow selecting the same file again
        return;
      }
      
      setSelectedAudio(file);
      
      // Show success message
      toast({
        title: 'Audio selected',
        description: `${file.name} has been selected. Press Enter or click Send to upload.`,
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleSendImageMessage = (imageFile, prompt) => {
    if (!conversationId) {
      toast({
        title: 'Error',
        description: 'No conversation ID available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Reset scroll tracking when user sends an image message
    setUserHasScrolled(false);

    // Add user message to chat history immediately with image preview
    const imagePreviewUrl = URL.createObjectURL(imageFile);
    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: prompt || 'Image uploaded',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      originalData: {
        metadata: {
          image_url: imagePreviewUrl,
        }
      }
    };
    addChatMessage(userMessage);

    // Add loading message
    const loadingMessage = {
      id: Date.now() + 1,
      type: 'ai',
      message: <ChatLoadingDots size="md" color="gray.500" />,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isLoading: true
    };
    addChatMessage(loadingMessage);

    // Clear input and selected image
    setNewMessage('');
    setSelectedImage(null);
    resetTextareaHeight();

    // Scroll to bottom after adding messages
    setTimeout(() => {
      if (isMobile && mobileMessagesRef.current) {
        mobileMessagesRef.current.scrollTo({
          top: mobileMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      } else if (!isMobile && desktopMessagesRef.current) {
        desktopMessagesRef.current.scrollTo({
          top: desktopMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);

    // Show toast notification that image is being analyzed
    toast({
      title: 'Image uploaded',
      description: 'We are analyzing your image. Please wait for the results',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });

    // Send the image message
    sendImageMessage(
      { conversationId, imageFile, prompt },
      {
        onSuccess: () => {
          // The API will invalidate queries and refetch messages, which will update the chat
          // The loading message will be replaced with the actual AI response
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to send image message',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        },
      }
    );
  };

  const handleSendVoiceMessage = (audioFile) => {
    if (!conversationId) {
      toast({
        title: 'Error',
        description: 'No conversation ID available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Reset scroll tracking when user sends a voice message
    setUserHasScrolled(false);

    // Add user message to chat history immediately with audio preview
    const audioPreviewUrl = URL.createObjectURL(audioFile);
    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: 'Audio message',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      originalData: {
        metadata: {
          voice_url: audioPreviewUrl,
          content_type: audioFile.type || 'audio/wav',
        }
      }
    };
    addChatMessage(userMessage);

    // Add loading message
    const loadingMessage = {
      id: Date.now() + 1,
      type: 'ai',
      message: <ChatLoadingDots size="md" color="gray.500" />,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isLoading: true
    };
    addChatMessage(loadingMessage);

    // Clear selected audio
    setSelectedAudio(null);

    // Scroll to bottom after adding messages
    setTimeout(() => {
      if (isMobile && mobileMessagesRef.current) {
        mobileMessagesRef.current.scrollTo({
          top: mobileMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      } else if (!isMobile && desktopMessagesRef.current) {
        desktopMessagesRef.current.scrollTo({
          top: desktopMessagesRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);

    // Send the voice message
    sendVoiceMessage(
      { conversationId, voiceFile: audioFile },
      {
        onSuccess: () => {
          // The API will invalidate queries and refetch messages, which will update the chat
          // The loading message will be replaced with the actual AI response
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to send voice message',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        },
      }
    );
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    // If there's a selected image, require both image and text
    if (selectedImage && !newMessage.trim()) {
      toast({
        title: 'Text required',
        description: 'Please enter a question or description about the image',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // If there's a selected audio file, send it (no text required for audio)
    if (selectedAudio) {
      handleSendVoiceMessage(selectedAudio);
      return;
    }
    
    if (newMessage.trim()) {
      // Reset scroll tracking when user sends a new message
      setUserHasScrolled(false);
      
      // Check which mode is active and send with appropriate message_type
      let messageType = 'text';
      
      if (isDiagnoseMode) {
        messageType = 'diagnose';
      } else if (isBuySellMode) {
        messageType = 'buy/sell';
      } else if (isHelpMode) {
        messageType = 'help';
      }
      
      // Create suggestion when sending a message
      // try {
      //   createSuggestion({
      //     prompt: newMessage.trim(),
      //     vehicle_id: selectedVehicle?.id || null,
      //     swimlane: messageType
      //   }, {
      //     onError: (error) => {
      //       console.error('Failed to create suggestion:', error);
      //       // Don't show error to user, just log it
      //     }
      //   });
      // } catch (error) {
      //   console.error('Error creating suggestion:', error);
      // }
      
      // Refetch suggestions after sending a message
      if (selectedVehicle?.id && messageType) {
        setTimeout(() => {
          refetchSuggestions();
        }, 500);
      }
      
      // If there's a selected image, send it with the message
      if (selectedImage) {
        handleSendImageMessage(selectedImage, newMessage);
      } else {
        // Use onChatSubmit to ensure loading message is added
        if (onChatSubmit) {
          onChatSubmit(newMessage, messageType);
        }
        
        setNewMessage('');
        resetTextareaHeight();
      }
      
      // Scroll to bottom after sending message
      setTimeout(() => {
        if (isMobile && mobileMessagesRef.current) {
          mobileMessagesRef.current.scrollTo({
            top: mobileMessagesRef.current.scrollHeight,
            behavior: 'smooth'
          });
        } else if (!isMobile && desktopMessagesRef.current) {
          desktopMessagesRef.current.scrollTo({
            top: desktopMessagesRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  // Auto-resize textarea function
  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Only adjust height for real DOM events (not synthetic events from suggestions)
    if (e.target && e.target.tagName === 'TEXTAREA') {
      // Auto-resize the textarea with a small delay for smoother experience
      setTimeout(() => {
        const textarea = e.target;
        // Only adjust height if we have a valid DOM element with style property
        if (textarea && textarea.style) {
          textarea.style.height = 'auto';
          const newHeight = Math.min(textarea.scrollHeight || 60, 120); // Max height of 120px
          textarea.style.height = newHeight + 'px';
        }
      }, 10);
    }
  };

  // Reset textarea height when message is sent
  const resetTextareaHeight = () => {
    if (mobileTextareaRef.current) {
      const mobileElement = mobileTextareaRef.current?.querySelector?.('textarea') || mobileTextareaRef.current;
      if (mobileElement && mobileElement.style) {
        mobileElement.style.height = '40px';
      }
    }
    if (desktopTextareaRef.current) {
      const desktopElement = desktopTextareaRef.current?.querySelector?.('textarea') || desktopTextareaRef.current;
      if (desktopElement && desktopElement.style) {
        desktopElement.style.height = '40px';
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Check if there's a loading message
  const isLoading = filteredMessages.some(msg => msg.isLoading) || isSendingMessage;

  return (
    <Flex direction="column" h="calc(100vh - 80px)" position="relative">
      {/* Debug Info */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Box p={2} bg="gray.100" fontSize="xs" color="gray.600">
          Action Buttons: {showActionButtons ? 'SHOWN' : 'HIDDEN'} | 
          Messages: {chatMessages?.length || 0} | 
          User Messages: {chatMessages?.filter(m => m.type === 'user').length || 0} |
          AI Messages: {chatMessages?.filter(m => m.type === 'ai' && !m.isLoading).length || 0}
          <Button 
            size="xs" 
            ml={2} 
            onClick={() => setShowActionButtons(!showActionButtons)}
            colorScheme="blue"
          >
            Toggle Buttons
          </Button>
        </Box>
      )} */}
      {isMobile ? (
        // Mobile Chat View
        <Flex direction="column" h="full" position="relative">
          {/* Chat Container - Mobile Style */}
          <Box 
            w="full" 
            maxW="400px" 
            flex={1}
            bg="white"
            borderRadius="lg"
            boxShadow="lg"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            mx="auto"
            mt={4}
            mb="100px" // Add bottom margin to prevent content from being hidden behind fixed input and Process Management button
          >
            {/* Chat Messages */}
            <Box 
              flex={1} 
              overflowY="auto" 
              px={4} 
              py={4} 
              pb={isLoggedIn && isMobile ? 20 : 4}
              ref={mobileMessagesRef} 
              onScroll={handleScroll} 
              position="relative"
              bg="white"
              sx={{
                WebkitOverflowScrolling: 'touch',
                overflowScrolling: 'touch',
                // Ensure proper height for sticky positioning on iOS Safari
                minHeight: '100%',
                // Force hardware acceleration for smooth scrolling
                WebkitTransform: 'translateZ(0)',
                transform: 'translateZ(0)',
                // Custom scrollbar color
                '&::-webkit-scrollbar-thumb': {
                  background: '#212E89',
                },
                // Firefox scrollbar color
                scrollbarColor: '#212E89 transparent',
              }}
            >
              {renderVehicleContextBanner()}
              <VStack spacing={4} align="stretch">
                {/* Show loading message when switching chats */}
                {messagesLoading && filteredMessages.length === 0 && conversationId && (
                  <HStack align="start" spacing={3}>
                    <Box maxW="80%">
                      <Box
                        bg="#EDEDED"
                        borderRadius="xl"
                        px={4}
                        py={3}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                      >
                        <Text fontSize="sm" color="gray.600">Loading...</Text>
                      </Box>
                    </Box>
                  </HStack>
                )}
                {filteredMessages.map((msg, index) => (
                  <Box key={msg.id ? `${msg.id}_${index}` : `msg_${index}`}>
                    {msg.type === 'ai' ? (
                      <HStack align="start" spacing={3}>
                        {/* <Avatar size="sm" bg="black" icon={<Text color="white" fontSize="xs">AI</Text>} /> */}
                        <Box maxW="80%">
                          <Box
                            bg={msg.isLoading ? "blue.100" : "#EDEDED"}
                            borderRadius="xl"
                            px={4}
                            py={3}
                            wordBreak="break-word"
                            overflowWrap="break-word"
                            overflow="hidden"
                          >
                            {typeof msg.message === 'string' ? (
                              renderMarkdownMessage(msg.message)
                            ) : (
                              <Box wordBreak="break-word" overflowWrap="break-word">
                                {msg.message}
                              </Box>
                            )}
                          </Box>
                          {msg.carInfo && (
                            <Box mt={3} border="1px" borderColor="blue.200" borderRadius="lg" p={4}>
                              <Image
                                src={msg.carInfo.image}
                                alt="Car"
                                borderRadius="md"
                                mb={3}
                              />
                              <Text fontWeight="bold" mb={3}>Key Highlights of the 2024 Model 3 Highland</Text>
                              <VStack spacing={2} align="stretch">
                                {msg.carInfo.highlights.map((highlight, index) => (
                                  <Flex key={index} justify="space-between" fontSize="sm">
                                    <Text fontWeight="medium">{highlight.feature}:</Text>
                                    <Text>{highlight.details}</Text>
                                  </Flex>
                                ))}
                              </VStack>
                            </Box>
                          )}
                          
                          
                        </Box>
                      </HStack>
                    ) : (
                      <HStack justify="end" spacing={3}>
                        <Box maxW="80%">
                          <Box
                            bg="blue.500"
                            color="white"
                            borderRadius="xl"
                            px={4}
                            py={3}
                            wordBreak="break-word"
                            overflowWrap="break-word"
                            overflow="hidden"
                          >
                            <Text fontSize="sm" wordBreak="break-word" overflowWrap="break-word" whiteSpace="pre-wrap">{msg.message}</Text>
                          </Box>
                          
                          {/* Display image if image_url exists */}
                          {msg.originalData && msg.originalData.metadata && msg.originalData.metadata.image_url && (
                            <Box mt={3} w="full">
                              <Image
                                src={msg.originalData.metadata.image_url}
                                alt="User uploaded image"
                                borderRadius="xl"
                                objectFit="cover"
                                w="full"
                                h="auto"
                                maxH="200px"
                                // border="2px"
                                // borderColor="blue.300"
                              />
                            </Box>
                          )}
                          
                          {/* Display audio player if voice_url exists */}
                          {msg.originalData && msg.originalData.metadata && msg.originalData.metadata.voice_url && (
                            <Box mt={3} w="full">
                              <WaveformAudioPlayer
                                src={msg.originalData.metadata.voice_url}
                                type={msg.originalData.metadata.content_type || 'audio/wav'}
                                isMobile={isMobile}
                              />
                            </Box>
                          )}


                          
                        </Box>
                        {/* <Avatar size="sm" bg="blue.500" name="Annie John" /> */}
                      </HStack>
                    )}
                  </Box>
                                 ))}
               </VStack>
               
               
               
               {/* Scroll to Bottom Button - Mobile */}
              {showScrollButton && (
                <IconButton
                  position="fixed"
                  bottom="100px"
                  right="4"
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                  onClick={scrollToBottom}
                  aria-label="Scroll to bottom"
                  icon={<Text fontSize="sm">↓</Text>}
                  boxShadow="lg"
                  _hover={{ transform: 'scale(1.1)' }}
                  transition="all 0.2s"
                  zIndex={20}
                />
              )}
            </Box>
          </Box>

            {/* Chat Input - Fixed to Bottom */}
            {showExpertChat && (
              <ExpertChatBot
                email={loggedInUserEmail}
                conversationId={conversationId}
                onClose={() => setShowExpertChat(false)}
                isMobile={isMobile}
                vehicle_id={selectedVehicle?.id}
              />
            )}
            <ChatInputSection
              isMobile={isMobile}
              isLoggedIn={isLoggedIn}
              isDiagnoseMode={isDiagnoseMode}
              isBuySellMode={isBuySellMode}
              onDiagnoseRepairVehicle={handleDiagnoseRepairVehicle}
              onBuySell={handleBuySell}
              onGoToGarage={handleGoToGarage}
              onHelpExpert={() => setShowExpertChat(true)}
              newMessage={newMessage}
              selectedImage={selectedImage}
              selectedAudio={selectedAudio}
              isLoading={isLoading}
              onMessageChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              onSendMessage={handleSendMessage}
              onImageButtonClick={handleImageButtonClick}
              onCarIconClick={handleCarIconClick}
              onSidebarToggle={handleSidebarToggle}
              onRemoveImage={() => setSelectedImage(null)}
              onRemoveAudio={() => setSelectedAudio(null)}
              fileInputRef={fileInputRef}
              audioInputRef={audioInputRef}
              textareaRef={mobileTextareaRef}
              handleImageSelect={handleImageSelect}
              handleAudioSelect={handleAudioSelect}
              suggestions={suggestions}
            />
        </Flex>
      ) : (
        // Desktop Chat View - Split Layout
        <Flex direction="column" h="full" position="relative">
          {/* Chat Messages Area - Split Layout */}
          <Box 
            flex={1} 
            overflowY="auto" 
            px={8} 
            py={6} 
            ref={desktopMessagesRef} 
            onScroll={handleScroll} 
            position="relative" 
            pb={isLoggedIn ? "180px" : "180px"}
            bg="white"
            sx={{
              WebkitOverflowScrolling: 'touch',
              overflowScrolling: 'touch',
              // Ensure proper height for sticky positioning on iOS Safari
              minHeight: '100%',
              // Force hardware acceleration for smooth scrolling
              WebkitTransform: 'translateZ(0)',
              transform: 'translateZ(0)',
              // Custom scrollbar color
              '&::-webkit-scrollbar-thumb': {
                background: '#212E89',
              },
              // Firefox scrollbar color
              scrollbarColor: '#212E89 transparent',
            }}
          >
            <VStack spacing={6} align="stretch" maxW="1200px" mx="auto">
              {renderVehicleContextBanner()}
              {/* Show loading message when switching chats */}
              {messagesLoading && filteredMessages.length === 0 && conversationId && (
                <HStack align="start" spacing={4} justify="start">
                  <Box maxW="60%">
                    <Box
                      bg="gray.100"
                      borderRadius="xl"
                      px={6}
                      py={4}
                      wordBreak="break-word"
                      overflowWrap="break-word"
                    >
                      <Text fontSize="md" color="gray.600">Loading...</Text>
                    </Box>
                  </Box>
                </HStack>
              )}
              {filteredMessages.map((msg, index) => (
                <Box key={msg.id ? `${msg.id}_${index}` : `msg_${index}`}>
                  {msg.type === 'ai' ? (
                    // AI Messages - Left Side
                    <HStack align="start" spacing={4} justify="start">
                      {/* <Avatar size="md" bg="black" icon={<Text color="white" fontSize="sm">AI</Text>} /> */}
                      <Box maxW="60%">
                        <Box
                          bg={msg.isLoading ? "blue.100" : "gray.100"}
                          borderRadius="xl"
                          px={6}
                          py={4}
                          wordBreak="break-word"
                          overflowWrap="break-word"
                          overflow="hidden"
                        >
                          {typeof msg.message === 'string' ? (
                            renderMarkdownMessage(msg.message)
                          ) : (
                            <Box wordBreak="break-word" overflowWrap="break-word">
                              {msg.message}
                            </Box>
                          )}
                        </Box>
                        {msg.carInfo && (
                          <Box mt={4} border="2px" borderColor="blue.200" borderRadius="xl" p={6}>
                            <Image
                              src={msg.carInfo.image}
                              alt="Car"
                              borderRadius="xl"
                              mb={4}
                              w="full"
                            />
                            <Text fontWeight="bold" fontSize="lg" mb={4}>Key Highlights of the 2024 Model 3 Highland</Text>
                            <VStack spacing={3} align="stretch">
                              {msg.carInfo.highlights.map((highlight, index) => (
                                <Flex key={index} justify="space-between" fontSize="md" py={2} borderBottom="1px" borderBottomColor="gray.200">
                                  <Text fontWeight="semibold">{highlight.feature}:</Text>
                                  <Text>{highlight.details}</Text>
                                </Flex>
                              ))}
                            </VStack>
                          </Box>
                            )}
                         
                                                   
                       </Box>
                    </HStack>
                  ) : (
                    // User Messages - Right Side
                    <HStack justify="end" spacing={4}>
                      <Box maxW="60%" display="flex" justify="end">
                        <Box w="100%">
                          <Box
                            bg="blue.500"
                            color="white"
                            borderRadius="xl"
                            px={6}
                            py={4}
                            wordBreak="break-word"
                            overflowWrap="break-word"
                            overflow="hidden"
                          >
                            <Text fontSize="md" wordBreak="break-word" overflowWrap="break-word" whiteSpace="pre-wrap">{msg.message}</Text>
                          </Box>
                          
                          {/* Display image if image_url exists */}
                          {msg.originalData && msg.originalData.metadata && msg.originalData.metadata.image_url && (
                            <Box mt={4} w="full">
                              <Image
                                src={msg.originalData.metadata.image_url}
                                alt="User uploaded image"
                                borderRadius="xl"
                                objectFit="cover"
                                w="full"
                                h="auto"
                                maxH="300px"
                                // border="2px"
                                // borderColor="blue.300"
                              />
                            </Box>
                          )}
                          
                          {/* Display audio player if voice_url exists */}
                          {msg.originalData && msg.originalData.metadata && msg.originalData.metadata.voice_url && (
                            <Box mt={4} w="full">
                              <WaveformAudioPlayer
                                src={msg.originalData.metadata.voice_url}
                                type={msg.originalData.metadata.content_type || 'audio/wav'}
                                isMobile={isMobile}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {/* <Avatar size="md" bg="blue.500" name="Annie John" /> */}
                    </HStack>
                  )}
                </Box>
                             ))}
             </VStack>
             
             
             
             {/* Scroll to Bottom Button - Desktop */}
            {showScrollButton && (
              <IconButton
                position="fixed"
                bottom="120px"
                right="8"
                size="md"
                colorScheme="blue"
                borderRadius="full"
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
                icon={<Text fontSize="md">↓</Text>}
                boxShadow="lg"
                _hover={{ transform: 'scale(1.1)' }}
                transition="all 0.2s"
                zIndex={20}
              />
            )}
          </Box>

            {/* Chat Input - Fixed at Bottom */}
            {showExpertChat && (
              <ExpertChatBot
                email={loggedInUserEmail}
                conversationId={conversationId}
                onClose={() => setShowExpertChat(false)}
                isMobile={isMobile}
                vehicle_id={selectedVehicle?.id}
              />
            )}
            <ChatInputSection
              isMobile={isMobile}
              isLoggedIn={isLoggedIn}
              isDiagnoseMode={isDiagnoseMode}
              isBuySellMode={isBuySellMode}
              onDiagnoseRepairVehicle={handleDiagnoseRepairVehicle}
              onBuySell={handleBuySell}
              onGoToGarage={handleGoToGarage}
              onHelpExpert={() => setShowExpertChat(true)}
              newMessage={newMessage}
              selectedImage={selectedImage}
              selectedAudio={selectedAudio}
              isLoading={isLoading}
              onMessageChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              onSendMessage={handleSendMessage}
              onImageButtonClick={handleImageButtonClick}
              onCarIconClick={handleCarIconClick}
              onSidebarToggle={handleSidebarToggle}
              onRemoveImage={() => setSelectedImage(null)}
              onRemoveAudio={() => setSelectedAudio(null)}
              fileInputRef={fileInputRef}
              audioInputRef={audioInputRef}
              textareaRef={desktopTextareaRef}
              handleImageSelect={handleImageSelect}
              handleAudioSelect={handleAudioSelect}
              suggestions={suggestions}
            />
        </Flex>
      )}

      {/* Car Sound Diagnosis Popup */}
      <CarSoundDiagnosisPopup isOpen={isOpen} onClose={onClose} />

      {/* Audio Upload Menu - iOS only */}
      <Modal isOpen={isAudioMenuOpen} onClose={onAudioMenuClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Upload Audio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} py={4}>
              <Button
                w="full"
                size="lg"
                colorScheme="blue"
                leftIcon={<Text fontSize="xl">🎤</Text>}
                onClick={handleRecordAudio}
              >
                Record Audio
              </Button>
              <Button
                w="full"
                size="lg"
                variant="outline"
                leftIcon={<Text fontSize="xl">📁</Text>}
                onClick={handleBrowseAudio}
              >
                Browse Files
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Audio Recording Modal */}
      <Modal isOpen={isRecordingModalOpen} onClose={cancelRecording} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Record Audio</ModalHeader>
          <ModalCloseButton onClick={cancelRecording} />
          <ModalBody>
            <VStack spacing={6} py={4}>
              {isRecording ? (
                <>
                  <Box textAlign="center">
                    <Text fontSize="3xl" fontWeight="bold" color="red.500" mb={2}>
                      ● Recording
                    </Text>
                    <Text fontSize="xl" color="gray.600">
                      {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </Text>
                  </Box>
                  <Button
                    size="lg"
                    colorScheme="red"
                    borderRadius="full"
                    w="80px"
                    h="80px"
                    onClick={stopRecording}
                    leftIcon={<Text fontSize="2xl">⏹</Text>}
                  >
                    Stop
                  </Button>
                </>
              ) : pendingRecording ? (
                <>
                  <Text textAlign="center" fontSize="md" color="gray.600">
                    Preview your recording below. Save it or record again.
                  </Text>
                  <audio
                    controls
                    src={URL.createObjectURL(pendingRecording)}
                    style={{ width: '100%' }}
                  />
                  <Text fontSize="sm" color="gray.500">
                    {pendingRecording.name} ({(pendingRecording.size / 1024 / 1024).toFixed(2)} MB)
                  </Text>
                </>
              ) : (
                <>
                  <Text textAlign="center" fontSize="md" color="gray.600">
                    Click the button below to start recording
                  </Text>
                  <Button
                    size="lg"
                    colorScheme="red"
                    borderRadius="full"
                    w="80px"
                    h="80px"
                    onClick={startRecording}
                    leftIcon={<Text fontSize="2xl">🎤</Text>}
                  >
                    Start
                  </Button>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            {pendingRecording && !isRecording && (
              <>
                <Button
                  size="sm"
                  colorScheme="blue"
                  mr={3}
                  onClick={handleSaveRecording}
                >
                  Save Recording
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  mr={3}
                  onClick={handleRetakeRecording}
                >
                  Record Again
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={cancelRecording} isDisabled={isRecording}>
              {pendingRecording ? 'Close' : 'Cancel'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ChatBox;